import React, { useEffect, useMemo, useState } from "react";
import {
  Box, Typography, Paper, Grid, Chip, Button, CircularProgress, Tabs, Tab,
  Table, TableHead, TableBody, TableRow, TableCell, Stack,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import api from "../../api";
import { adminHasPermission } from "../../utils/adminPermissions";

const PURPLE = "#7c3aed";

function formatMode(mode) {
  if (!mode) return "—";
  const s = String(mode).toLowerCase();
  if (s === "in_person") return "In person";
  if (s === "online") return "Online";
  return mode;
}

function statusChip(status) {
  const s = String(status || "").toLowerCase();
  if (s === "active") return { label: "Active", bg: "#dcfce7", fg: "#166534" };
  if (s === "upcoming") return { label: "Upcoming", bg: "#dbeafe", fg: "#1d4ed8" };
  if (s === "completed") return { label: "Completed", bg: "#e5e7eb", fg: "#374151" };
  return { label: status || "—", bg: "#e5e7eb", fg: "#374151" };
}

function DetailField({ label, value }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 600 }}>
        {label}
      </Typography>
      <Typography variant="body1" fontWeight={600} sx={{ mt: 0.25 }}>
        {value ?? "—"}
      </Typography>
    </Box>
  );
}

export default function CourseView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const canEdit = adminHasPermission("update-courses");
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [course, setCourse] = useState(null);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    api.get(`admin/assign/course/${id}`)
      .then((res) => {
        setCourse(res.data?.data || null);
      })
      .catch(() => {
        toast.error("Failed to load course");
        setCourse(null);
      })
      .finally(() => setLoading(false));
  }, [id]);

  const sessionsByGroup = useMemo(() => {
    const sessions = course?.class_sessions || [];
    const map = new Map();
    sessions.forEach((s) => {
      const key = `${s.mode_name || "—"} · ${s.location_name || "—"}`;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(s);
    });
    return [...map.entries()];
  }, [course?.class_sessions]);

  if (loading) {
    return (
      <Box sx={{ p: 4, display: "flex", justifyContent: "center" }}>
        <CircularProgress sx={{ color: PURPLE }} />
      </Box>
    );
  }

  if (!course) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography color="text.secondary">Course not found.</Typography>
        <Button sx={{ mt: 2 }} onClick={() => navigate("/admin/course-list")}>Back to courses</Button>
      </Box>
    );
  }

  const groups = course.groups || [];
  return (
    <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 }, bgcolor: "#f8fafc", minHeight: "100vh" }}>
      <Paper
        elevation={0}
        sx={{
          p: 2.5, mb: 2, borderRadius: 3,
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          color: "#fff",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <Box>
          <Typography variant="h5" fontWeight={700}>Course Details</Typography>
          <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
            View course details, groups and enrollment.
          </Typography>
        </Box>
        {canEdit && (
          <Button
            variant="contained"
            startIcon={<EditIcon />}
            onClick={() => navigate(`/admin/course/${id}/edit`)}
            sx={{
              bgcolor: "rgba(255,255,255,0.2)",
              color: "#fff",
              border: "1px solid rgba(255,255,255,0.35)",
              textTransform: "none",
              fontWeight: 600,
              "&:hover": { bgcolor: "rgba(255,255,255,0.3)" },
            }}
          >
            Edit Course
          </Button>
        )}
      </Paper>

      <Paper elevation={0} sx={{ borderRadius: 3, border: "1px solid #eef0f4", overflow: "hidden" }}>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          sx={{
            px: 2, borderBottom: "1px solid #eef0f4",
            "& .MuiTab-root": { textTransform: "uppercase", fontWeight: 700, fontSize: 12 },
            "& .Mui-selected": { color: PURPLE },
            "& .MuiTabs-indicator": { bgcolor: PURPLE },
          }}
        >
          <Tab label="Course details" />
          <Tab label="Groups & enrollment" />
        </Tabs>

        <Box sx={{ p: { xs: 2, md: 3 } }}>
          {tab === 0 && (
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6} md={3}>
                <DetailField label="Course name" value={course.name} />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <DetailField label="Academic year" value={course.academic_year} />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <DetailField label="School year" value={course.school_year_name} />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <DetailField label="Total seats" value={course.total_seats ?? "—"} />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <DetailField label="Available seats" value={course.remaining_seats ?? "—"} />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <DetailField label="Start date" value={course.start_date} />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <DetailField label="End date" value={course.end_date} />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary" sx={{ textTransform: "uppercase", fontWeight: 600 }}>
                  Modes
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                  {(course.modes || []).map((m) => (
                    <Chip key={m.id} label={m.name} size="small" sx={{ bgcolor: "#ede9fe", color: PURPLE, fontWeight: 600 }} />
                  ))}
                  {!course.modes?.length && (
                    <Typography variant="body2">—</Typography>
                  )}
                </Stack>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary" sx={{ textTransform: "uppercase", fontWeight: 600 }}>
                  Locations
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                  {(course.locations || []).map((l, i) => (
                    <Chip
                      key={l.id || l.name || i}
                      label={typeof l === "string" ? l : (l.name || "—")}
                      size="small"
                      sx={{ bgcolor: "#fef3c7", color: "#b45309", fontWeight: 600 }}
                    />
                  ))}
                  {!course.locations?.length && (
                    <Typography variant="body2">—</Typography>
                  )}
                </Stack>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary" sx={{ textTransform: "uppercase", fontWeight: 600 }}>
                  Subjects
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                  {(course.subjects || []).map((s) => (
                    <Chip key={s.id} label={s.name} size="small" sx={{ bgcolor: "#dbeafe", color: "#1d4ed8", fontWeight: 600 }} />
                  ))}
                </Stack>
              </Grid>

              <Grid item xs={12} md={6}>
                <DetailField
                  label="General features"
                  value={(course.features_names || []).join(", ") || "—"}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <DetailField
                  label="Online features"
                  value={(course.online_features_names || []).join(", ") || "—"}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <DetailField
                  label="In-person features"
                  value={(course.in_person_features_names || []).join(", ") || "—"}
                />
              </Grid>

              {sessionsByGroup.length > 0 && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>
                    Subject schedule
                  </Typography>
                  <Grid container spacing={2}>
                    {sessionsByGroup.map(([groupLabel, sessions]) => (
                      <Grid item xs={12} md={6} key={groupLabel}>
                        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                            {groupLabel}
                          </Typography>
                          <Stack spacing={1}>
                            {sessions.map((s) => (
                              <Box
                                key={s.id}
                                sx={{ p: 1.25, borderRadius: 1.5, bgcolor: "#f8fafc", border: "1px solid #eef2f7" }}
                              >
                                <Typography variant="body2" fontWeight={600}>{s.subject_name}</Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {[s.held_weekday_name, s.held_time, s.duration_minutes ? `${s.duration_minutes} min` : null]
                                    .filter(Boolean)
                                    .join(" · ")}
                                </Typography>
                              </Box>
                            ))}
                          </Stack>
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>
                </Grid>
              )}
            </Grid>
          )}

          {tab === 1 && (
            <Box>
              {groups.length === 0 ? (
                <Typography variant="body2" color="text.secondary">No groups configured for this course.</Typography>
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700, color: "#64748b" }}>Group name</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: "#64748b" }}>Mode</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: "#64748b" }}>Location</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: "#64748b" }}>Time period</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, color: "#64748b" }}>Max seats</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, color: "#64748b" }}>Enrolled</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, color: "#64748b" }}>Available seats</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: "#64748b" }}>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {groups.map((g) => {
                      const st = statusChip(g.status);
                      return (
                        <TableRow key={g.course_plan_id} hover>
                          <TableCell sx={{ fontWeight: 600 }}>{g.plan_name}</TableCell>
                          <TableCell>{formatMode(g.mode)}</TableCell>
                          <TableCell>{g.location || "—"}</TableCell>
                          <TableCell>{g.start_date} – {g.end_date}</TableCell>
                          <TableCell align="right">{g.max_seats ?? "—"}</TableCell>
                          <TableCell align="right">{g.enrolled_count ?? 0}</TableCell>
                          <TableCell align="right">
                            {g.available_seats != null ? g.available_seats : "—"}
                          </TableCell>
                          <TableCell>
                            <Chip size="small" label={st.label} sx={{ bgcolor: st.bg, color: st.fg, fontWeight: 600 }} />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </Box>
          )}
        </Box>
      </Paper>
    </Box>
  );
}
